import { useEffect, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  BookOpen,
  Check,
  Copy,
  Download,
  FileText,
  Info,
  Loader2,
  Terminal,
  X,
  type LucideIcon,
} from 'lucide-react';
import { formatSkillSize, type SkillSummary } from '@blogforge/shared';
import { api } from '../hooks/api.js';
import { Portal } from './ui/Portal.js';

// ─────────────────────────────────────────────────────────────────
// Skills — the always-visible section at the bottom of the sidebar.
//
// A "skill" here is a markdown file the user hands to their own AI assistant:
// the Content Generator (what BlogForger can open) and API Access (what an
// agent may do to this workspace over the API). Both are data, not UI copy —
// the catalog comes from `GET /api/skills`, so the row, the version badge and
// the panel all describe the file the download button actually returns.
//
// The ⓘ opens the explanation: what it is, how to use it, tips, and an
// example prompt to copy. That panel is the reason this feature is useful —
// a download with no context is a mystery file.
// ─────────────────────────────────────────────────────────────────

const SKILL_ICONS: Record<string, LucideIcon> = {
  'content-generator': FileText,
  'api-access': Terminal,
};

export function SkillsSection() {
  const skills = useQuery({
    queryKey: ['skills'],
    queryFn: () => api.skills.list(),
    // The catalog only changes when the deployment does.
    staleTime: 5 * 60_000,
  });

  const [openSkill, setOpenSkill] = useState<SkillSummary | null>(null);
  const [error, setError] = useState<string | null>(null);
  const list = skills.data?.skills ?? [];

  const download = async (skill: SkillSummary) => {
    setError(null);
    try {
      await api.skills.download(skill);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
  };

  if (list.length === 0) return null;

  return (
    <div className="mx-3 pb-2">
      <div className="mb-1.5 flex items-center gap-1.5 px-2">
        <BookOpen size={12} className="text-muted" />
        <span className="text-muted text-[10.5px] font-semibold tracking-wide uppercase">
          Skills
        </span>
      </div>

      <div className="space-y-0.5">
        {list.map((skill) => (
          <SkillRow
            key={skill.id}
            skill={skill}
            onInfo={() => setOpenSkill(skill)}
            onDownload={() => void download(skill)}
          />
        ))}
      </div>

      {error && (
        <p className="border-danger/20 bg-danger/8 text-danger mt-1.5 rounded-lg border px-2 py-1.5 text-[11px]">
          {error}
        </p>
      )}

      {openSkill && (
        <Portal>
          <SkillInfoModal skill={openSkill} onClose={() => setOpenSkill(null)} />
        </Portal>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// One row: name, ⓘ, download
// ─────────────────────────────────────────────────────────────────
export function SkillRow({
  skill,
  onInfo,
  onDownload,
}: {
  skill: SkillSummary;
  onInfo: () => void;
  onDownload: () => void;
}) {
  const Icon = SKILL_ICONS[skill.id] ?? BookOpen;
  const size = formatSkillSize(skill.sizeBytes);

  return (
    <div className="group hover:bg-ink/5 flex items-center gap-2 rounded-xl px-2 py-1.5 transition-colors">
      <Icon size={14} className="text-muted group-hover:text-ink-soft shrink-0 transition-colors" />

      <button
        type="button"
        onClick={onInfo}
        className="text-ink-soft group-hover:text-ink min-w-0 flex-1 truncate text-left text-[12.5px] font-medium transition-colors"
      >
        {skill.name}
        {size && <span className="text-muted ml-1.5 text-[10.5px] font-normal">{size}</span>}
      </button>

      <button
        type="button"
        onClick={onInfo}
        aria-label={`How to use the ${skill.name} skill`}
        title={`What is ${skill.name}?`}
        className="text-muted hover:bg-ink/5 hover:text-ink shrink-0 rounded-md p-1 transition-colors"
      >
        <Info size={13} />
      </button>

      <button
        type="button"
        onClick={onDownload}
        disabled={!skill.available}
        aria-label={`Download the ${skill.name} skill`}
        title={skill.available ? `Download ${skill.filename}` : (skill.unavailableReason ?? '')}
        className="text-muted hover:bg-ink/5 hover:text-ink shrink-0 rounded-md p-1 transition-colors disabled:cursor-not-allowed disabled:opacity-40"
      >
        <Download size={13} />
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// The ⓘ panel: what it is, how to use it, tips, an example prompt
//
// Sized for reading, not for the rail it is opened from: the overlay covers
// the viewport, the panel is up to 672px wide, and steps and tips sit in two
// columns on anything wider than a phone. Rendered through Portal by
// SkillsSection — see ui/Portal.tsx for why it cannot live inside the rail.
// ─────────────────────────────────────────────────────────────────
export function SkillInfoModal({ skill, onClose }: { skill: SkillSummary; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const size = formatSkillSize(skill.sizeBytes);
  const Icon = SKILL_ICONS[skill.id] ?? BookOpen;

  // Focus moves into the panel and Escape closes it: the panel is opened from
  // a keyboard-reachable row, so leaving focus behind in the sidebar would
  // strand anyone navigating by keyboard. Effects do not run in a static
  // render, so this is inert under `renderToStaticMarkup`.
  useEffect(() => {
    panelRef.current?.focus();
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const copyPrompt = async () => {
    if (!skill.howTo.examplePrompt) return;
    await navigator.clipboard.writeText(skill.howTo.examplePrompt);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const download = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.skills.download(skill);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    // `modal-backdrop` is the app's shared overlay (fixed inset-0, z-60 — above
    // the sidebar rail's z-50 — scrim, blur and fade-in). The card is a flex
    // column with its own scrolling body so the header and footer stay put
    // while a long skill scrolls, and `max-h` keeps it inside a short viewport.
    <div className="modal-backdrop">
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={`${skill.name} skill`}
        className="modal-card flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden p-6 outline-none"
      >
        {/* ── Header ─────────────────────────────────────── */}
        <div className="flex items-start gap-3">
          <div className="bg-accent/10 text-accent flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl">
            <Icon size={17} />
          </div>
          <div className="min-w-0 flex-1">
            <h3 className="text-ink text-[17px] font-bold tracking-tight">{skill.name} skill</h3>
            <p className="text-muted mt-0.5 text-[12.5px]">
              {skill.version ? `v${skill.version}` : 'Skill file'}
              {skill.author ? ` · ${skill.author}` : ''}
              {size ? ` · ${size}` : ''}
              {skill.lineCount ? ` · ${skill.lineCount} lines` : ''}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="btn-ghost h-8 w-8 rounded-lg px-0"
          >
            <X size={15} />
          </button>
        </div>

        {/* ── Scrollable body ────────────────────────────── */}
        <div className="mt-5 min-h-0 flex-1 overflow-y-auto pr-1">
          <p className="text-ink-soft text-[13px] leading-relaxed">{skill.howTo.intro}</p>

          <div className="mt-6 grid gap-6 sm:grid-cols-2">
            <section>
              <h4 className="text-ink mb-2.5 text-[13px] font-bold">How to use it</h4>
              <ol className="space-y-2.5">
                {skill.howTo.steps.map((step, i) => (
                  <li key={i} className="flex gap-2.5">
                    <span className="bg-ink/5 text-ink-soft mt-px flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10.5px] font-bold">
                      {i + 1}
                    </span>
                    <span className="text-ink-soft text-[13px] leading-relaxed">{step}</span>
                  </li>
                ))}
              </ol>
            </section>

            <section>
              <h4 className="text-ink mb-2.5 text-[13px] font-bold">Tips</h4>
              <ul className="space-y-2">
                {skill.howTo.tips.map((tip, i) => (
                  <li key={i} className="text-ink-soft flex gap-2.5 text-[13px] leading-relaxed">
                    <span className="bg-accent mt-[8px] h-1 w-1 shrink-0 rounded-full" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {skill.howTo.examplePrompt && (
            <section className="mt-6">
              <div className="mb-2 flex items-center justify-between gap-2">
                <h4 className="text-ink text-[13px] font-bold">Example prompt</h4>
                <button
                  type="button"
                  onClick={() => void copyPrompt()}
                  className="btn-ghost px-2 py-1 text-[11.5px]"
                >
                  {copied ? <Check size={12} /> : <Copy size={12} />}
                  {copied ? 'Copied' : 'Copy'}
                </button>
              </div>
              <p className="border-line bg-card-2 text-ink-soft rounded-lg border p-3.5 text-[12.5px] leading-relaxed">
                {skill.howTo.examplePrompt}
              </p>
            </section>
          )}

          {!skill.available && (
            <p className="border-warn/30 bg-warn/10 text-ink-soft mt-5 rounded-lg border px-3 py-2 text-[12.5px]">
              {skill.unavailableReason}
            </p>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────── */}
        {error && (
          <p className="border-danger/20 bg-danger/8 text-danger mt-3 rounded-lg border px-3 py-2 text-[12px]">
            {error}
          </p>
        )}

        <div className="border-line mt-4 flex flex-wrap items-center gap-2 border-t pt-4">
          <p className="text-muted min-w-0 flex-1 truncate font-mono text-[11px]">
            {skill.filename}
          </p>
          <button type="button" onClick={onClose} className="btn-ghost px-3 py-2 text-[12.5px]">
            Close
          </button>
          <button
            type="button"
            onClick={() => void download()}
            disabled={busy || !skill.available}
            className="btn-primary px-3 py-2 text-[12.5px]"
          >
            {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
            {busy ? 'Downloading…' : 'Download skill'}
          </button>
        </div>
      </div>
    </div>
  );
}
